import { useState } from "react";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import SearchSelect, {
  type SearchSelectOption,
} from "@components/FormsV2/SearchSelect";
import { useItemOptions, useItemsById } from "./api/useItemPickers";
import { ITEM_TYPES, type ItemType } from "./types";
import "./style.css";

export interface ItemPickerProps {
  itemType: ItemType;
  itemId: string;
  onItemTypeChange: (t: ItemType) => void;
  onItemIdChange: (id: string) => void;
}

interface ItemSelectProps {
  itemType: ItemType;
  itemId: string;
  onItemIdChange: (id: string) => void;
}

const ItemSelect = ({
  itemType,
  itemId,
  onItemIdChange,
}: ItemSelectProps): JSX.Element => {
  const select = useItemOptions(itemType);
  const [picked, setPicked] = useState<SearchSelectOption | null>(null);

  // A saved item may not be on the pages loaded so far, so look it up by id.
  const isPicked = itemId !== "" && picked?.value === itemId;
  const lookup = useItemsById(
    itemId && !isPicked ? { [itemType]: [itemId] } : {}
  );
  const saved = lookup(itemType, itemId);
  const value = !itemId
    ? null
    : isPicked
    ? picked
    : saved
    ? { value: saved.id, label: saved.label }
    : select.options.find((o) => o.value === itemId) ?? null;

  return (
    <SearchSelect
      id="recommendation-item"
      label="Item"
      value={value}
      options={select.options}
      onChange={(option) => {
        setPicked(option);
        onItemIdChange(option?.value ?? "");
      }}
      onInputChange={select.onInputChange}
      onMenuScrollToBottom={select.onMenuScrollToBottom}
      isLoading={select.isLoading}
      placeholder="Choose an item…"
      required
    />
  );
};

export const ItemPicker = ({
  itemType,
  itemId,
  onItemTypeChange,
  onItemIdChange,
}: ItemPickerProps): JSX.Element => (
  <div className="row">
    <div className="col-sm-4">
      <RequiredReactSelect
        label="Item Type"
        value={ITEM_TYPES.find((o) => o.value === itemType)}
        onChange={(opt: any) =>
          onItemTypeChange((opt?.value ?? "Task") as ItemType)
        }
        options={ITEM_TYPES}
        placeholder="Select…"
        required
      />
    </div>
    <div className="col-sm-8">
      {/* Each type searches its own endpoint, so start fresh on a change. */}
      <ItemSelect
        key={itemType}
        itemType={itemType}
        itemId={itemId}
        onItemIdChange={onItemIdChange}
      />
    </div>
  </div>
);

export default ItemPicker;
